import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/Button";
import { X, Code } from "lucide-react";
import LanguageDropdown from "../../components/ui/LanguageDropdown";
import { GlobalService } from "../../modules/shared/services";

interface TranslationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: TranslationFormData) => Promise<void>;
  selectedTranslation?: any;
  selectedContentForTranslation?: any;
  languages?: any[];
}

interface TranslationFormData {
  content: string;
  title: string;
  language: any;
  contentManagementType: any;
}

const TranslationModal: React.FC<TranslationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedTranslation,
  selectedContentForTranslation,
  languages = []
}) => {
  const [formData, setFormData] = useState<TranslationFormData>({
    content: "",
    title: "",
    language: null,
    contentManagementType: selectedContentForTranslation || null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [contentType, setContentType] = useState("text");

  // Reset form when modal opens/closes or translation changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        content: selectedTranslation?.content || "",
        title: selectedTranslation?.title || "",
        language: selectedTranslation?.language || null,
        contentManagementType: selectedContentForTranslation || null
      });
      setErrors({});
    }
  }, [isOpen, selectedTranslation, selectedContentForTranslation]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.language) {
      newErrors.language = "Please select a language";
    }

    if (!formData.title.trim()) {
      newErrors.title = "Please enter a title";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Please enter content";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave(formData);
      onClose();
    } catch (error: any) {
      console.error("Failed to save translation:", error);
      // Show user-friendly error message
      const errorMessage = error?.message || "An unexpected error occurred. Please try again.";
      GlobalService.showToast("error", "Translation Error", `Failed to save translation: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLanguageSelect = (language: any) => {
    setFormData(prev => ({
      ...prev,
      language: language
    }));
    // Clear language error when language is selected
    if (errors.language) {
      setErrors(prev => ({
        ...prev,
        language: ""
      }));
    }
  };

  const handleInputChange = (field: keyof TranslationFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  if (!isOpen) return null;

  // Don't render if no content is selected for translation
  if (!selectedContentForTranslation) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            {selectedTranslation ? "Edit Translation" : "Add Translation"}
          </h2>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Language Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language <span className="text-red-500">*</span>
            </label>
            <LanguageDropdown
              selectedLanguage={formData.language}
              onLanguageSelect={handleLanguageSelect}
              placeholder="Select language..."
              className="w-full"
              disabled={isSubmitting}
            />
            {errors.language && (
              <p className="text-red-500 text-sm mt-1">{errors.language}</p>
            )}
          </div>

          {/* Title Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.title ? "border-red-500" : "border-gray-300"
              } focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter translation title"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              disabled={isSubmitting}
            />
            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div>

          {/* Content Textarea */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Content <span className="text-red-500">*</span>
              </label>
              <div className="flex rounded-md overflow-hidden border">
                <button
                  type="button"
                  className={`px-3 py-1 text-xs ${contentType === "text" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                  onClick={() => setContentType("text")}
                  disabled={isSubmitting}
                >
                  Text
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 text-xs ${contentType === "html" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                  onClick={() => setContentType("html")}
                  disabled={isSubmitting}
                >
                  HTML
                </button>
              </div>
            </div>
            <div className="flex items-start gap-2">
              {contentType === "html" && <Code className="h-5 w-5 text-gray-400 mt-2" />}
              <textarea
                rows={6}
                className={`w-full px-3 py-2 border rounded-lg resize-none ${
                  errors.content ? "border-red-500" : "border-gray-300"
                } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${contentType === "html" ? "font-mono" : ""}`}
                placeholder="Enter translated content"
                value={formData.content}
                onChange={(e) => handleInputChange("content", e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {contentType === "html" && (
              <p className="text-xs text-gray-500 mt-1">HTML formatting is supported in this field.</p>
            )}
            {errors.content && (
              <p className="text-red-500 text-sm mt-1">{errors.content}</p>
            )}
          </div>

          {/* Content Info */}
          {selectedContentForTranslation && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Translating Content:</h4>
              <p className="text-sm text-gray-600">
                {selectedContentForTranslation.contentTitle || "Untitled Section"}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="min-w-[120px]"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {selectedTranslation ? "Updating..." : "Adding..."}
                </>
              ) : (
                selectedTranslation ? "Update Translation" : "Add Translation"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranslationModal;
